/**
 * tib-form-validator.js
 * TeeInBlue form validation.
 *
 * Two kinds of character groups in BLOCK_GROUPS below:
 *   - kind: 'autoReplace'  Silent 1:1 character swap. Used for lookalikes
 *                          (Cyrillic/Greek letters that visually mimic Latin) where
 *                          the user usually can't see the bad char. We rewrite the
 *                          value in place and show a small auto-dismissing pill.
 *   - kind: 'block'        No printable equivalent (hearts, emoji). Shows inline
 *                          error + a "remove unprintable characters" button. Blocks
 *                          add-to-cart submit until cleared.
 *
 * Adding a new group: append one entry to BLOCK_GROUPS + one matching key under
 * tib_validation in locales/*.json. No other code changes needed.
 *
 * Triggers: input/change/focus/blur events, MutationObserver on #teeFormInputs,
 * TeeInBlue's own teeinblue-event-* events, a 750ms poll fallback for programmatic
 * .value writes (e.g. TIB song-search API), and a submit-capture safety net.
 *
 * Translations are passed via window.__TIB_VALIDATION_TEXTS (set by the
 * snippets/tib-form-validator.liquid wrapper).
 */

(function () {
  'use strict';

  var DEBUG = false;
  var POLL_INTERVAL_MS = 750;
  var POLL_IDLE_TIMEOUT_MS = 5000;
  var VALIDATE_DEBOUNCE_MS = 100;
  var CORRECTED_PILL_TIMEOUT_MS = 4000;
  var FIX_DONE_TIMEOUT_MS = 2500;
  var ERROR_RESOLVED_FADE_DELAY_MS = 5000;
  var ERROR_RESOLVED_HIDE_DELAY_MS = 5600; // a bit after the fade transition completes

  function log() {
    if (DEBUG) console.log.apply(console, ['[TIB Validator]'].concat(Array.prototype.slice.call(arguments)));
  }

  // ---------------------------------------------------------------------------
  // Character group registry
  // ---------------------------------------------------------------------------

  var BLOCK_GROUPS = {
    lookalikes: {
      kind: 'autoReplace',
      // Map each forbidden code point to its printable Latin equivalent.
      // Extend this map as new lookalikes are discovered in production.
      replacements: {
        // Cyrillic letters that look like Latin letters
        'а': 'a', 'е': 'e', 'о': 'o', 'р': 'p',
        'с': 'c', 'х': 'x', 'у': 'y', 'і': 'i',
        'А': 'A', 'Е': 'E', 'О': 'O', 'Р': 'P',
        'С': 'C', 'Х': 'X', 'У': 'Y', 'І': 'I',
        'ӏ': 'i', 'є': 'e', 'ә': 'a',
        // Greek lookalikes
        'ο': 'o', 'α': 'a', 'ε': 'e', 'υ': 'u',
        'Ο': 'O', 'Α': 'A', 'Ε': 'E', 'Ρ': 'P',
        'Τ': 'T', 'Χ': 'X',
        // "Weird e" variants (extend as encountered)
        'ė': 'e', 'ℯ': 'e', 'ҭ': 'e',
        // Fullwidth Latin (sometimes pasted from CJK keyboards)
        'ａ': 'a', 'ｅ': 'e', 'ｏ': 'o', 'ｉ': 'i',
        'ｕ': 'u'
      },
      messageKey: 'lookalikes_corrected'
    },
    hearts: {
      kind: 'block',
      pattern: /[❤♥❣♡\u{1F490}\u{1F493}\u{1F494}\u{1F495}\u{1F496}\u{1F497}\u{1F498}\u{1F499}\u{1F49A}\u{1F49B}\u{1F49C}\u{1F49D}\u{1F49E}\u{1F49F}\u{1F5A4}\u{1F90D}\u{1F90E}\u{1FA77}\u{1FA78}\u{1FA79}]️?/gu,
      messageKey: 'hearts'
    },
    emoji: {
      kind: 'block',
      // Misc symbols (☀-⛿) + Dingbats (✀-➿) + pictographs/emoji planes.
      // Hearts group runs first via its own pattern; remaining emoji caught here.
      pattern: /[☀-➿\u{1F300}-\u{1F5FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FAFF}]️?/gu,
      messageKey: 'emoji'
    }
    // Future example — block accented Latin letters:
    // accents: { kind: 'autoReplace',
    //            replacements: { 'é':'e','è':'e','ê':'e','ë':'e','à':'a','á':'a' },
    //            messageKey: 'accents_corrected' }
  };

  // ---------------------------------------------------------------------------
  // i18n
  // ---------------------------------------------------------------------------

  var DEFAULT_TEXTS = {
    hearts: "Hearts can't be printed. Remove: {{ chars }}",
    emoji: "Emoji and symbols can't be printed. Remove: {{ chars }}",
    lookalikes_corrected: "{{ count }} invisible characters were auto-corrected",
    fix_link: "Remove unprintable characters",
    fix_done: "Unprintable characters removed",
    submit_blocked: "Please fix the highlighted fields before adding to cart."
  };

  function t(key, vars) {
    var texts = window.__TIB_VALIDATION_TEXTS || {};
    var template = texts[key] || DEFAULT_TEXTS[key] || key;
    if (!vars) return template;
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      return vars[name] != null ? String(vars[name]) : '';
    });
  }

  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ---------------------------------------------------------------------------
  // Core: autoCorrect / validate / applyFix
  // ---------------------------------------------------------------------------

  // Build a single combined regex for all autoReplace groups for fast detection.
  function buildAutoReplaceRegex() {
    var chars = '';
    Object.keys(BLOCK_GROUPS).forEach(function (key) {
      var group = BLOCK_GROUPS[key];
      if (group.kind !== 'autoReplace') return;
      Object.keys(group.replacements).forEach(function (ch) {
        chars += ch.replace(/[-\\\]^]/g, '\\$&');
      });
    });
    return chars ? new RegExp('[' + chars + ']', 'gu') : null;
  }

  var AUTO_REPLACE_REGEX = buildAutoReplaceRegex();

  function lookupReplacement(ch) {
    var keys = Object.keys(BLOCK_GROUPS);
    for (var i = 0; i < keys.length; i++) {
      var group = BLOCK_GROUPS[keys[i]];
      if (group.kind === 'autoReplace' && group.replacements[ch] != null) {
        return group.replacements[ch];
      }
    }
    return null;
  }

  // Returns { changed: bool, count: int, value: string }
  function autoCorrectString(value) {
    if (!AUTO_REPLACE_REGEX || !value) return { changed: false, count: 0, value: value };
    var count = 0;
    var newValue = value.replace(AUTO_REPLACE_REGEX, function (ch) {
      var repl = lookupReplacement(ch);
      if (repl == null) return ch;
      count++;
      return repl;
    });
    return { changed: count > 0, count: count, value: newValue };
  }

  // Apply autoCorrect to an input element in place, preserving cursor.
  function autoCorrect(input) {
    var result = autoCorrectString(input.value);
    if (!result.changed) return 0;

    var start = null, end = null;
    try {
      start = input.selectionStart;
      end = input.selectionEnd;
    } catch (e) { /* selection not supported on this input type */ }

    input.value = result.value;

    if (start != null && end != null) {
      try { input.setSelectionRange(start, end); } catch (e) { /* ignore */ }
    }

    // Let TIB and any other listeners see the cleaned value
    input.dispatchEvent(new Event('input', { bubbles: true }));
    return result.count;
  }

  // Returns { groups: ['hearts','emoji'], chars: { hearts: ['❤'], emoji: ['🌟'] } }
  function validate(value) {
    var groups = [];
    var chars = {};
    if (!value) return { groups: groups, chars: chars };

    Object.keys(BLOCK_GROUPS).forEach(function (key) {
      var group = BLOCK_GROUPS[key];
      if (group.kind !== 'block') return;
      group.pattern.lastIndex = 0;
      var matches = value.match(group.pattern);
      if (matches && matches.length) {
        groups.push(key);
        // Dedupe matches for this group
        var seen = {};
        var unique = [];
        matches.forEach(function (m) {
          if (!seen[m]) { seen[m] = true; unique.push(m); }
        });
        chars[key] = unique;
      }
    });
    return { groups: groups, chars: chars };
  }

  // Strip all 'block' group matches from an input.
  function applyFix(input) {
    var value = input.value;
    Object.keys(BLOCK_GROUPS).forEach(function (key) {
      var group = BLOCK_GROUPS[key];
      if (group.kind !== 'block') return;
      group.pattern.lastIndex = 0;
      value = value.replace(group.pattern, '');
    });
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // ---------------------------------------------------------------------------
  // DOM: per-field feedback UI
  // ---------------------------------------------------------------------------

  var fieldUid = 0;
  var trackedFields = new Map(); // input -> { container, errorEl, fixBtn, correctedEl, lastSeenValue, debounceId, correctedTimeoutId, fixDoneTimeoutId }

  function isTrackableInput(el) {
    if (!el || !el.tagName) return false;
    if (el.closest && !el.closest('.tee-field')) return false;
    var tag = el.tagName;
    if (tag === 'TEXTAREA') return true;
    if (tag !== 'INPUT') return false;
    var type = (el.getAttribute('type') || 'text').toLowerCase();
    return type === 'text' || type === '' || type === 'search';
  }

  function ensureTracked(input) {
    if (trackedFields.has(input)) return trackedFields.get(input);

    var container = input.closest('.tee-field') || input.parentNode;
    if (!container) return null;

    // Remove any stale feedback element TIB may have left behind from a prior
    // input that has since been swapped out.
    var stale = container.querySelectorAll('.tib-validation-feedback');
    for (var s = 0; s < stale.length; s++) stale[s].remove();

    var uid = 'tib-validation-' + (++fieldUid);

    var feedback = document.createElement('div');
    feedback.className = 'tib-validation-feedback';
    feedback.setAttribute('data-tib-validation-for', uid);

    var errorEl = document.createElement('span');
    errorEl.className = 'tib-validation-error';
    errorEl.id = uid + '-error';
    errorEl.setAttribute('role', 'alert');
    errorEl.hidden = true;

    var fixBtn = document.createElement('button');
    fixBtn.type = 'button';
    fixBtn.className = 'tib-validation-fix-link';
    fixBtn.textContent = t('fix_link');
    fixBtn.hidden = true;

    var correctedEl = document.createElement('span');
    correctedEl.className = 'tib-validation-corrected';
    correctedEl.setAttribute('role', 'status');
    correctedEl.hidden = true;

    feedback.appendChild(errorEl);
    feedback.appendChild(fixBtn);
    feedback.appendChild(correctedEl);

    container.appendChild(feedback);

    var record = {
      input: input,
      container: container,
      errorEl: errorEl,
      fixBtn: fixBtn,
      correctedEl: correctedEl,
      uid: uid,
      lastSeenValue: input.value,
      debounceId: null,
      correctedTimeoutId: null,
      fixDoneTimeoutId: null,
      errorFadeTimeoutId: null,
      errorHideTimeoutId: null,
      hadError: false,
      fadeScheduled: false
    };

    fixBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      applyFix(input);
      input.focus();
      validateField(input);
      showFixDone(record);
    });

    trackedFields.set(input, record);
    log('Tracking new field', uid, input);
    return record;
  }

  function untrackRemovedFields() {
    trackedFields.forEach(function (record, input) {
      if (!document.body.contains(input)) {
        if (record.debounceId) clearTimeout(record.debounceId);
        if (record.correctedTimeoutId) clearTimeout(record.correctedTimeoutId);
        if (record.fixDoneTimeoutId) clearTimeout(record.fixDoneTimeoutId);
        if (record.errorFadeTimeoutId) clearTimeout(record.errorFadeTimeoutId);
        if (record.errorHideTimeoutId) clearTimeout(record.errorHideTimeoutId);
        // Drop the feedback DOM so a stale error message can't outlive its input
        var feedback = record.errorEl && record.errorEl.parentNode;
        if (feedback && feedback.parentNode) feedback.parentNode.removeChild(feedback);
        trackedFields.delete(input);
      }
    });
  }

  function showCorrected(record, count) {
    record.correctedEl.textContent = t('lookalikes_corrected', { count: count });
    record.correctedEl.hidden = false;
    if (record.correctedTimeoutId) clearTimeout(record.correctedTimeoutId);
    record.correctedTimeoutId = setTimeout(function () {
      record.correctedEl.hidden = true;
    }, CORRECTED_PILL_TIMEOUT_MS);
  }

  function showFixDone(record) {
    record.correctedEl.textContent = t('fix_done');
    record.correctedEl.hidden = false;
    if (record.fixDoneTimeoutId) clearTimeout(record.fixDoneTimeoutId);
    record.fixDoneTimeoutId = setTimeout(function () {
      record.correctedEl.hidden = true;
    }, FIX_DONE_TIMEOUT_MS);
  }

  function renderErrors(record, result) {
    var input = record.input;

    if (result.groups.length === 0) {
      // Field is clean. Drop aria-invalid + fix button immediately so the red
      // border and CTA disappear, but let the message text linger for 5s as
      // confirmation of what was just fixed, then fade and hide.
      input.setAttribute('aria-invalid', 'false');
      input.removeAttribute('aria-describedby');
      record.fixBtn.hidden = true;

      // Nothing to fade — already hidden
      if (record.errorEl.hidden) {
        record.hadError = false;
        return;
      }
      // Fade already in progress — don't restart it
      if (record.fadeScheduled) return;

      record.fadeScheduled = true;
      record.errorFadeTimeoutId = setTimeout(function () {
        record.errorEl.classList.add('tib-validation-error--resolved');
      }, ERROR_RESOLVED_FADE_DELAY_MS);
      record.errorHideTimeoutId = setTimeout(function () {
        record.errorEl.hidden = true;
        record.errorEl.innerHTML = '';
        record.errorEl.classList.remove('tib-validation-error--resolved');
        record.fadeScheduled = false;
        record.errorFadeTimeoutId = null;
        record.errorHideTimeoutId = null;
        record.hadError = false;
      }, ERROR_RESOLVED_HIDE_DELAY_MS);
      return;
    }

    // Has errors — cancel any pending fade so the error stays visible at full opacity
    if (record.errorFadeTimeoutId) { clearTimeout(record.errorFadeTimeoutId); record.errorFadeTimeoutId = null; }
    if (record.errorHideTimeoutId) { clearTimeout(record.errorHideTimeoutId); record.errorHideTimeoutId = null; }
    record.fadeScheduled = false;
    record.errorEl.classList.remove('tib-validation-error--resolved');

    var SENTINEL = '___TIBCHARS___';
    var lines = result.groups.map(function (groupKey) {
      var charList = (result.chars[groupKey] || []).map(function (c) {
        return '<span class="tib-validation-chars">' + escapeHTML(c) + '</span>';
      }).join(' ');
      var msg = t(groupKey, { chars: SENTINEL });
      var safe = escapeHTML(msg).replace(SENTINEL, charList);
      return '<div>' + safe + '</div>';
    });

    record.errorEl.innerHTML = lines.join('');
    record.errorEl.hidden = false;
    record.fixBtn.hidden = false;
    record.fixBtn.textContent = t('fix_link');
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', record.uid + '-error');
    record.hadError = true;
  }

  // ---------------------------------------------------------------------------
  // Per-field pipeline
  // ---------------------------------------------------------------------------

  function processField(input) {
    var record = ensureTracked(input);
    if (!record) return;

    var correctedCount = autoCorrect(input);
    if (correctedCount > 0) showCorrected(record, correctedCount);

    record.lastSeenValue = input.value;

    var result = validate(input.value);
    renderErrors(record, result);
  }

  function validateField(input) {
    var record = ensureTracked(input);
    if (!record) return;
    var result = validate(input.value);
    renderErrors(record, result);
    record.lastSeenValue = input.value;
  }

  function scheduleProcess(input) {
    var record = ensureTracked(input);
    if (!record) return;
    // autoCorrect must be synchronous to preserve cursor; only debounce the
    // validate/render step. So: run autoCorrect now, schedule render.
    var correctedCount = autoCorrect(input);
    if (correctedCount > 0) showCorrected(record, correctedCount);
    record.lastSeenValue = input.value;

    if (record.debounceId) clearTimeout(record.debounceId);
    record.debounceId = setTimeout(function () {
      record.debounceId = null;
      var result = validate(input.value);
      renderErrors(record, result);
    }, VALIDATE_DEBOUNCE_MS);
  }

  // ---------------------------------------------------------------------------
  // Discovery: scan the DOM for TIB inputs we should track
  // ---------------------------------------------------------------------------

  function scanForInputs(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('.tee-field input, .tee-field textarea');
    var found = 0;
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (!isTrackableInput(el)) continue;
      if (!trackedFields.has(el)) {
        processField(el); // validates initial / prefilled value
        found++;
      }
    }
    if (found > 0) log('Discovered', found, 'new TIB inputs');
  }

  // ---------------------------------------------------------------------------
  // Polling fallback for programmatic .value = ... writes (e.g. TIB song-search)
  // ---------------------------------------------------------------------------

  var pollIntervalId = null;
  var lastTibActivityAt = Date.now();

  function noteActivity() { lastTibActivityAt = Date.now(); }

  function startPolling() {
    if (pollIntervalId != null) return;
    log('Polling started');
    pollIntervalId = setInterval(function () {
      if (Date.now() - lastTibActivityAt > POLL_IDLE_TIMEOUT_MS && trackedFields.size === 0) {
        stopPolling();
        return;
      }
      trackedFields.forEach(function (record, input) {
        if (!document.body.contains(input)) return;
        if (input.value !== record.lastSeenValue) {
          log('Polling detected programmatic change', record.uid);
          processField(input);
          noteActivity();
        }
      });
      // Also discover newly-injected fields the MutationObserver may have missed
      // (e.g. iframe boundary, shadow DOM corners).
      scanForInputs(document);
    }, POLL_INTERVAL_MS);
  }

  function stopPolling() {
    if (pollIntervalId == null) return;
    clearInterval(pollIntervalId);
    pollIntervalId = null;
    log('Polling stopped (idle)');
  }

  // ---------------------------------------------------------------------------
  // Submit guard
  // ---------------------------------------------------------------------------

  var submitNoticeShownAt = 0;

  function findFirstInvalid() {
    var first = null;
    trackedFields.forEach(function (record, input) {
      if (first) return;
      if (input.getAttribute('aria-invalid') === 'true') first = input;
    });
    return first;
  }

  function showSubmitNotice(submitBtn) {
    if (Date.now() - submitNoticeShownAt < 3000) return;
    submitNoticeShownAt = Date.now();
    var notice = document.createElement('div');
    notice.className = 'tib-validation-submit-notice';
    notice.setAttribute('role', 'alert');
    notice.textContent = t('submit_blocked');
    var anchor = (submitBtn && submitBtn.parentNode) || document.body;
    anchor.insertBefore(notice, submitBtn ? submitBtn.nextSibling : null);
    setTimeout(function () { if (notice.parentNode) notice.remove(); }, 5000);
  }

  function onSubmitCapture(e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    // Only intervene on forms that actually contain a TIB field
    var hasTib = false;
    trackedFields.forEach(function (_, input) {
      if (!hasTib && form.contains(input)) hasTib = true;
    });
    if (!hasTib) return;

    // Final pass — revalidate every tracked field in this form
    trackedFields.forEach(function (_, input) {
      if (form.contains(input)) processField(input);
    });

    var firstInvalid = findFirstInvalid();
    if (!firstInvalid) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    var submitBtn = form.querySelector('[type="submit"]');
    showSubmitNotice(submitBtn);

    try {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (_) {
      firstInvalid.scrollIntoView();
    }
    firstInvalid.focus();
  }

  // ---------------------------------------------------------------------------
  // Bootstrap
  // ---------------------------------------------------------------------------

  function shouldInitialize() {
    return document.querySelector('[id^="tee-"], .tee-field, #teeFormInputs, [class*="teeinblue"]') !== null
      // Bootstrap anyway on product pages — TIB may inject later
      || document.body.classList.contains('template-product')
      || document.querySelector('product-form') !== null;
  }

  function init() {
    log('Initializing');

    var eventOpts = { passive: true, capture: false };

    // Live events
    document.addEventListener('input', function (e) {
      if (isTrackableInput(e.target)) {
        noteActivity();
        scheduleProcess(e.target);
      }
    }, eventOpts);

    document.addEventListener('change', function (e) {
      if (isTrackableInput(e.target)) {
        noteActivity();
        processField(e.target);
      }
    }, eventOpts);

    document.addEventListener('focusin', function (e) {
      if (isTrackableInput(e.target)) {
        noteActivity();
        processField(e.target);
      }
    }, eventOpts);

    document.addEventListener('focusout', function (e) {
      if (isTrackableInput(e.target)) {
        noteActivity();
        processField(e.target);
      }
    }, eventOpts);

    // TIB lifecycle events — re-scan all fields when TIB signals a change
    var rescan = function () {
      noteActivity();
      scanForInputs(document);
      trackedFields.forEach(function (_, input) { processField(input); });
      startPolling();
    };
    document.addEventListener('teeinblue-event-page-changed', rescan, eventOpts);
    document.addEventListener('teeinblue-event-component-injected', rescan, eventOpts);

    // DOM observer — catch field injection
    var observer = new MutationObserver(function (mutations) {
      var dirty = false;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.addedNodes && m.addedNodes.length) { dirty = true; break; }
        if (m.removedNodes && m.removedNodes.length) { dirty = true; break; }
      }
      if (!dirty) return;
      untrackRemovedFields();
      scanForInputs(document);
      noteActivity();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Submit guard (capture so it runs before product-form.js)
    document.addEventListener('submit', onSubmitCapture, true);

    // Initial sweep + start polling for programmatic fills
    scanForInputs(document);
    startPolling();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      if (shouldInitialize()) init();
    });
  } else if (shouldInitialize()) {
    init();
  }
})();
